import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import PageMeta from "../../components/common/PageMeta";
import { useEffect, useState } from "react";
import Spinner from "../../components/ui/Spinner";
import {
  API_BASE_URL,
  API_URL,
  DEFAULT_PAGE_DESCRIPTION,
  DEFAULT_PAGE_TITLE,
  DEFAULT_PER_PAGE,
} from "../../utilities/constants";
import { NavLink, useLocation, useNavigate } from "react-router";
import TableFilter from "./TableFilter";
import useQueryParams from "../../hooks/useQueryParams";
import { handleApiCall, jsonToQueryParams } from "../../utilities/helper";
import CategoryTableFilter from "./CategoryTableFilter";
import Pagination from "../../components/ui/Pagination";
import ProductTableFilter from "./ProductTableFilter";


const CatalogPage = () => {
  const [loading, setLoading] = useState(true);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const location = useLocation();
  const categoryId = location.state?.categoryId ? location.state?.categoryId : null;
  const [selectedCategory, setSelectedCategory] = useState(categoryId);
  const navigate = useNavigate();
  const ID = location.state?.user ? location.state?.user?.id : location.state?.merchantId;
  const data = location.state?.user ? location.state?.user : "";
  const [selectedMerchantId, setSelectedMerchantId] = useState(ID);
  const [categories, setCategories] = useState([]);
  const { queryParams } = useQueryParams();
  const [page, setPage] = useState(0);
  const pageSize = queryParams.get("size") || DEFAULT_PER_PAGE;
  const sortOrder = queryParams.get("sortOrder") || "DESC";
  const [controller, setController] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState(null);
  const [categorySearch, setCategorySearch] = useState(queryParams.get("categorySearch") || "");

  const handleCategorySearch = (value) => {
    setCategorySearch(value);
    navigate(`/catalog?${jsonToQueryParams({
      categorySearch: value,
      page,
      sortOrder,
    })}`);
  };

  // Reset category search
  const handleCategoryReset = () => {
    setCategorySearch("");
    navigate(`/catalog?${jsonToQueryParams({
      productSearch,
      page,
      sortOrder,
    })}`);
  };
  // const categorySearch = queryParams.get("searchCategory") || "";
  const categoryQuery = jsonToQueryParams({ search: categorySearch });


  const callListApi = async () => {
    setCategoryLoading(true);
    try {
      const response = await handleApiCall(
        `${API_BASE_URL}/api/1.0.0/category?page=0&size=1000&sortBy=createdAt&direction=${sortOrder}&${categoryQuery}`,
        { method: "GET" }
      );

      if (response.responseStatus) {
        setCategories(response.data.content || []);
      } else {
        setCategories([]);
      }
    } catch (e) {
      console.error("Error", e);
    } finally {
      setCategoryLoading(false);
    }
  };

  useEffect(() => {
    if (selectedMerchantId) {
      setCategories([]);
      callListApi();
    }
  }, [selectedMerchantId, categoryQuery]);

  const productSearch = queryParams.get("searchProduct") || "";
  const productPage = parseInt(queryParams.get("page") || "0", 10);
  const productQuery = jsonToQueryParams({ search: productSearch });
  const callProductsApi = async () => {
    controller?.abort();
    const newController = new AbortController();
    setController(newController);
    const { signal } = newController;
    setLoading(true);

    try {
      const response = await handleApiCall(
        `${API_BASE_URL}/api/1.0.0/products?categoryId=${selectedCategory}&page=${productPage}&size=${pageSize}&sortBy=createdAt&direction=${sortOrder}&${productQuery}`,
        {
          method: "GET",
        },
        null,
        signal
      );
      if (response.responseStatus === true) {
        setFilteredProducts(response.data);
      } else {
        setFilteredProducts(null);
      }

      if (response.responseCode !== 299) {
        setLoading(false);
      }
    } catch (e) {
      console.error("Error fetching products", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCategory) {
      callProductsApi();
    }
  }, [selectedCategory, productQuery, productPage]);

  return (
    <>
      <PageMeta title={`Catalog - ${DEFAULT_PAGE_TITLE}`} description={`Catalog - ${DEFAULT_PAGE_DESCRIPTION}`} />
      <div className="flex">
        <PageBreadcrumb pageTitle="Catalog" />
      </div>
      <div className="flex justify-between items-center mb-6">
        <TableFilter
          onMerchantTypeChange={setSelectedMerchantId}
          type={data}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="col-span-12 sm:col-span-6 lg:col-span-4 bg-gray-50 dark:bg-gray-800 p-6 border-gray-400 border-none rounded-xl shadow-md">
          <div className="mb-4">
            <CategoryTableFilter
              categorySearch={categorySearch}
              onSearch={handleCategorySearch}
              onReset={handleCategoryReset}
            />
          </div>
          {categoryLoading ? (
            <div className="flex justify-center items-center">
              <Spinner />
            </div>
          ) : (
            <div className="relative sm:rounded-lg max-h-[70vh] overflow-y-auto">
              <table className="w-full text-sm text-left text-gray-600 dark:text-gray-300">
                <thead className="text-xs text-gray-700 uppercase bg-gray-200 dark:bg-gray-700 dark:text-gray-400">
                  <tr>
                    <th className="px-4 py-3">Name</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((cat) => (
                    <tr
                      key={cat.id}
                      className={`border-b cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 ${selectedCategory?.id === cat.id ? "bg-brand-100 dark:bg-brand-700" : ""}`}
                      onClick={() => setSelectedCategory(cat.id)}
                    >
                      <td className="underline px-4 py-3 font-medium text-gray-900 dark:text-white">
                        {cat.name}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {categoryLoading && (
                <div className="flex justify-center p-3 text-gray-500">Loading...</div>
              )}
            </div>
          )}

        </div>


        {/* Products Section */}
        <div className="col-span-12 sm:col-span-6 lg:col-span-8 bg-white dark:bg-gray-900 p-6 rounded-xl shadow-g">
          {selectedCategory ? (
            <>
              <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-3">
                <h2 className="text-lg font-bold text-gray-800 dark:text-white">
                  Products {selectedCategory.title}
                </h2>
                <div className="mb-4">
                  <ProductTableFilter />
                </div>
              </div>

              {loading ? (
                <div className="flex justify-center items-center">
                  <Spinner />
                </div>
              ) : (
                <div className="relative sm:rounded-lg max-h-[70vh] overflow-y-auto overflow-x-auto">
                  <table className="min-w-full text-sm text-left text-gray-500 dark:text-gray-400">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-400">
                      <tr>
                        <th className="px-6 py-3">Product Title</th>
                        <th className="px-6 py-3">Brand</th>
                        <th className="px-6 py-3">Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts?.content?.length > 0 ? (
                        filteredProducts?.content.map((prod) => (
                          <tr
                            key={prod.id}
                            className="border-b dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800"
                          >
                            <td className="underline px-6 py-4 font-medium text-gray-900 dark:text-white">
                              <NavLink
                                to={`/product-details/${prod.id}?${jsonToQueryParams({
                                  // selectedMerchantId:selectedMerchantId,
                                  searchCategory: categorySearch,
                                  searchProduct: productSearch,
                                  page: productPage,
                                  sortOrder
                                })} `} state={{ merchantId: selectedMerchantId, categoryId: selectedCategory, catalogData: data }}
                              >
                                {prod.title}
                              </NavLink>

                              {/* <NavLink to={`/product-details/${prod.id}`}>{prod.title}</NavLink> */}
                            </td>
                            <td className="px-6 py-4">{prod.brand}</td>
                            <td className="px-6 py-4">{prod.category?.name}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="3"
                            className="px-6 py-4 text-center text-gray-500 dark:text-gray-400"
                          >
                            No products found for this category
                          </td>
                        </tr>
                      )}
                    </tbody>

                  </table>
                </div>
              )}
              <div className='flex justify-center items-center'>
                <Pagination
                  pageParam="page"
                  currentPage={filteredProducts?.pageable?.pageNumber + 1} // convert 0-based to 1-based
                  totalElements={filteredProducts?.totalElements}
                />
              </div>
            </>
          ) : (
            <p className="text-gray-500">Select a category to see products</p>
          )}
        </div>
      </div>
    </>
  );

};

export default CatalogPage;
